import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ISiteDesign } from '@fi-shared/models';
import { fiSetBrandingColor, fiSetContrastColor } from '@fi-shared/utils/branding';
import { FiImageSize } from '@fi-shared/types/image-size';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';

@Component({
    selector: 'fi-customer-settings-preview-dialog',
    templateUrl: './customer-settings-preview-dialog.component.html',
    styleUrls: ['./customer-settings-preview-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-customer-settings-preview-dialog' },
    standalone: false,
})
export class FiCustomerSettingsPreviewDialogComponent
    extends FiDialogsContentBase<ISiteDesign, boolean>
    implements OnInit
{
    @ViewChild('desktopImage', { static: true }) desktopImage: ElementRef;
    @ViewChild('desktopBg', { static: true }) desktopBg: ElementRef;
    @ViewChild('desktopLogin', { read: ElementRef, static: false }) desktopLogin: ElementRef;
    @ViewChild('mobileImage', { static: true }) mobileImage: ElementRef;
    @ViewChild('mobileBg', { static: true }) mobileBg: ElementRef;
    @ViewChild('mobileLogin', { read: ElementRef, static: false }) mobileLogin: ElementRef;

    renderer: Renderer2;
    sanitizer: DomSanitizer;
    mediaStorageService: FiMediaStorageService;
    elementRef: ElementRef;
    desktopImageLoaded: boolean = false;
    mobileImageLoaded: boolean = false;

    private readonly defaultColor: string = '#ffffff';

    ngOnInit() {
        this.setServices();
        this.setPreviewImagesAndColor();
    }

    private setServices() {
        this.renderer = this.injector.get(Renderer2);
        this.sanitizer = this.injector.get(DomSanitizer);
        this.mediaStorageService = this.injector.get(FiMediaStorageService);
        this.elementRef = this.injector.get(ElementRef);
        this.injector.get(ChangeDetectorRef).detectChanges();
    }

    desktopLoaded() {
        this.desktopImageLoaded = true;
    }

    getDesktopStyleTop(): string {
        const rect: DOMRect = (this.desktopImage.nativeElement as HTMLImageElement).getBoundingClientRect();
        return `${rect.height * 0.109}px`;
    }

    getDesktopStyleLeft(): string {
        const die = this.desktopImage.nativeElement as HTMLImageElement,
            rect = die.getBoundingClientRect();
        return `${die.offsetLeft + rect.width * 0.071}px`;
    }

    getDesktopStyleHeight(): string {
        if (this.desktopImageLoaded) {
            this.renderer.addClass(this.desktopBg.nativeElement, 'loaded');
        }
        const rect: DOMRect = (this.desktopImage.nativeElement as HTMLImageElement).getBoundingClientRect();
        return `${rect.height * 0.58}px`;
    }

    getDesktopStyleWidth(): string {
        const rect: DOMRect = (this.desktopImage.nativeElement as HTMLImageElement).getBoundingClientRect();
        return `${rect.width * 0.851}px`;
    }

    getDesktopLoginStyleTransform(): SafeStyle {
        const scale: number = this.getDesktopLoginWidthK();
        return this.sanitizer.bypassSecurityTrustStyle(`scale(${scale}) translateX(-50%)`);
    }

    mobileLoaded() {
        this.mobileImageLoaded = true;
    }

    getMobileStyleTop(): string {
        const rect: DOMRect = (this.mobileImage.nativeElement as HTMLImageElement).getBoundingClientRect();
        return `${rect.height * 0.117}px`;
    }

    getMobileStyleLeft(): string {
        const die = this.mobileImage.nativeElement as HTMLImageElement,
            rect: DOMRect = die.getBoundingClientRect();
        return `${die.offsetLeft + rect.width * 0.115}px`;
    }

    getMobileStyleHeight(): string {
        if (this.mobileImageLoaded) {
            this.renderer.addClass(this.mobileImage.nativeElement, 'loaded');
        }

        const rect: DOMRect = (this.mobileImage.nativeElement as HTMLImageElement).getBoundingClientRect();
        return `${rect.height * 0.775}px`;
    }

    getMobileStyleWidth(): string {
        const rect = (this.mobileImage.nativeElement as HTMLImageElement).getBoundingClientRect();
        return `${rect.width * 0.78}px`;
    }

    getMobileLoginStyleTransform(): SafeStyle {
        const scale: number = this.getMobileLoginWidthK();
        return this.sanitizer.bypassSecurityTrustStyle(`scale(${scale}) translateX(-50%)`);
    }

    getMobileAppFontSize(): SafeStyle {
        const scale: number = this.getMobileLoginWidthK();
        return this.sanitizer.bypassSecurityTrustStyle(`${scale * 2.3 * 16}px`);
    }

    private setPreviewImagesAndColor() {
        if (this.data.BrandingColor) {
            const brandingStyle: HTMLStyleElement = fiSetBrandingColor(
                this.data.BrandingColor,
                'fi-customer-settings-preview-dialog',
                this.elementRef.nativeElement,
            );

            // note: didn't change AttachmentPreview to Small, because of file is absent
            const landscapeBackground: string = this.data.LandscapeReference
                ? this.getMediaFileUrl(this.data.LandscapeReference, FiImageSize.Background)
                : this.defaultColor;

            const logoBackground: string = this.data.LogoReference
                ? this.getMediaFileUrl(this.data.LogoReference, FiImageSize.AttachmentPreview)
                : this.defaultColor;

            brandingStyle.appendChild(
                document.createTextNode(`:root{ 
                --fi-branded-image-landscape: ${landscapeBackground};
                --fi-branded-image-logo: ${logoBackground};
            }`),
            );
        }

        if (this.data.ContrastColor) {
            fiSetContrastColor(
                this.data.ContrastColor,
                'fi-customer-settings-preview-dialog',
                this.elementRef.nativeElement,
            );
        }
    }

    private getMediaFileUrl(mediaBankFileReference: IMediaBankFileReference, imageSize: FiImageSize) {
        return `url("${this.mediaStorageService.getMediaFileUrl(mediaBankFileReference, { size: imageSize })}")`;
    }

    private getDesktopLoginWidthK(): number {
        const scale: number = this.desktopBg.nativeElement.offsetWidth / 1800;
        return scale < 1 ? scale : 1;
    }

    private getMobileLoginWidthK(): number {
        const scale: number = this.mobileBg.nativeElement.offsetWidth / 600;
        return scale < 1 ? scale : 1;
    }
}
